import { z } from 'zod'

export const gymPlanSchema = z
  .object({
    name: z.string().min(1, 'Name is required').max(200),
    plan_type: z.enum(['day_pass', 'entry_based', 'duration_based']),
    price: z.number().finite().min(0, 'Price must be 0 or greater'),
    currency: z
      .string()
      .length(3, 'Currency must be a 3-letter ISO code')
      .default('RON')
      .transform((v) => v.toUpperCase()),
    duration_days: z.number().int().min(1).optional(),
    max_entries: z.number().int().min(1).nullable().optional(),
    promo_tag: z.string().max(100).nullable().optional(),
    is_active: z.boolean().default(true),
    payable_online: z.boolean().default(false),
    // Held alongside a main plan (a session with an instructor, say) rather than
    // replacing it. Never grants entry, and never sold online.
    is_addon: z.boolean().default(false),
    // Lets the holder book classes on the gym's calendar.
    includes_instructor: z.boolean().default(false),
    // Paid by a partner (ESX, 7card...) per visit, not by the member. Both set
    // together or both null (gym_plans_external_complete).
    external_provider: z.string().trim().max(100).nullable().optional(),
    external_visit_price: z.number().finite().min(0).nullable().optional(),
  })
  .superRefine((data, ctx) => {
    // Mirrors gym_plans_addon_not_online.
    if (data.payable_online && data.is_addon) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['payable_online'],
        message: 'Add-ons cannot be sold online',
      })
    }
    const hasProvider = !!data.external_provider
    const hasRate = data.external_visit_price !== null && data.external_visit_price !== undefined
    if (hasProvider !== hasRate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [hasProvider ? 'external_visit_price' : 'external_provider'],
        message: 'An external provider needs both a name and a price per visit',
      })
    }
    // Mirrors gym_plans_external_not_online: the member is not the one paying.
    if (hasProvider && data.payable_online) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['payable_online'],
        message: 'Plans paid by an external provider cannot be sold online',
      })
    }
  })

export type GymPlanInput = z.infer<typeof gymPlanSchema>
